import {
  getUvInsertTextListKeys,
  isUvInsertTextListYear,
  UV_INSERT_TEXT_LIST_BULLET_DEFAULTS,
  type UVInsertFaceElementTextListConfig,
} from "../../models/uv-inserts"

/**
 * One row of a `TEXT_LIST` as both renderers draw it.
 *
 * `headline` is the first printed line and `moment` the optional second; `index` is the row's
 * 1-based position in the element's key sequence, kept so a caller can tie a rendered row back to
 * its `textByKey` keys after blank rows have been closed up.
 */
export type UvInsertTextListRow = {
  /** 1-based position in the element's key sequence (`${monthKeyPrefix}${index}`). */
  index: number
  month: string
  /** `''` unless the customer typed a full four-digit year. */
  year: string
  /** `''` when the customer left the moment blank — the row then prints its headline alone. */
  moment: string
  /** Line 1: `"Month Year"`, or just `"Month"` when there is no usable year. */
  headline: string
  /** Bullet colour for this row, or `undefined` when the config sets no colour at all. */
  bulletColor: string | undefined
}

export type GetUvInsertTextListRowsOptions = {
  element: UVInsertFaceElementTextListConfig
  /** The host-populated text; only this element's month/year/moment keys are read. */
  textByKey: Record<string, string>
}

/**
 * `colorByMonth` matched on the trimmed, case-folded month label.
 *
 * The table is authored in Liquid by hand and the month labels come from the stone data, so the
 * two can disagree on case or stray whitespace; a miss there would silently drop the birthstone
 * colour rather than fail, which is exactly the kind of bug nobody notices until print.
 */
function lookupColorByMonth(
  colorByMonth: Record<string, string> | undefined,
  month: string
): string | undefined {
  if (!colorByMonth) return undefined
  const wanted = month.trim().toLowerCase()
  if (!wanted) return undefined
  for (const [key, value] of Object.entries(colorByMonth)) {
    if (key.trim().toLowerCase() === wanted) return value
  }
  return undefined
}

/**
 * The rows a `TEXT_LIST` element prints, in order, for the DOM list and the canvas cart preview
 * alike — one builder so the two renderers cannot drift apart (they each had their own copy).
 *
 * Rules the printed card depends on:
 * - month, year and moment are trimmed;
 * - a row whose month is blank is **dropped** rather than left as a gap, so a skipped stone closes
 *   up and the remaining rows keep their original order;
 * - a year that is not exactly four digits is treated as absent — a renderer must never print a
 *   half-typed year such as `201`;
 * - `headline` joins month and year with a single space (the design prints "January 2017").
 *
 * @param options - Options object.
 * @param options.element - The parsed `TEXT_LIST` element config.
 * @param options.textByKey - The host-populated text keyed by line item property key.
 * @returns The rows to render, gaps closed, in key order.
 */
export function getUvInsertTextListRows({
  element,
  textByKey,
}: GetUvInsertTextListRowsOptions): UvInsertTextListRow[] {
  // A star has its own fill, so an unknown month falls back to that rather than the text colour.
  const fallbackColor =
    element.bullet.shape === "star"
      ? (element.bullet.color ?? element.bullet.fillColor ?? UV_INSERT_TEXT_LIST_BULLET_DEFAULTS.fillColor)
      : (element.bullet.color ?? element.color)

  return getUvInsertTextListKeys({ element })
    .map(({ index, monthKey, yearKey, momentKey }) => {
      const month = (textByKey[monthKey] ?? "").trim()
      const rawYear = yearKey ? (textByKey[yearKey] ?? "").trim() : ""
      const year = isUvInsertTextListYear(rawYear) ? rawYear : ""

      return {
        index,
        month,
        year,
        moment: (textByKey[momentKey] ?? "").trim(),
        headline: year ? `${month} ${year}` : month,
        bulletColor: lookupColorByMonth(element.bullet.colorByMonth, month) ?? fallbackColor,
      }
    })
    .filter((row) => row.month !== "")
}
